import React, { useState } from "react";
import { ArrowRight, ArrowUpRight, Check, ClipboardCheck, PackageSearch, Users } from "lucide-react";
import { Link, useRoute } from "wouter";
import { QuoteBand } from "@/components/QuoteBand";
import { StructuredData } from "@/components/StructuredData";
import {
  accessoryComparisonBrandVisuals,
  accessoryComparisonSelectionGuide,
  getAccessoryComparisonSectionsForGroup,
} from "@/data/accessoryEquivalencies";
import {
  accessoryCategoryBrowserBrands,
  getAccessoryCategoryBrowserCounts,
  getAccessoryCategoryBrowserFeaturedProducts,
  type AccessoryCategoryBrowserBrand,
  type AccessoryCategoryBrowserGroupId,
} from "@/data/accessoryCategoryCatalog";
import { getAccessoryComparisonProductImage } from "@/data/accessoryProductImages";
import { formatAccessoryCurrency } from "@/lib/accessoryQuoteCart";
import { accessoryCategoryDetails } from "@/data/resources";
import { accessoryGroups, realPhotos, siteConfig } from "@/data/site";
import NotFound from "./NotFound";

const categoryPhotos = [
  realPhotos.accessories.grabBars,
  realPhotos.accessories.handDryers,
  realPhotos.accessories.paperDispensers,
  realPhotos.accessories.mirrors,
  realPhotos.projects.blue,
  realPhotos.accessories.restroom,
];

export default function AccessoryCategory() {
  const [, params] = useRoute<{ category: string }>("/commercial-restroom-accessories/:category");
  const detail = accessoryCategoryDetails.find((item) => item.id === params?.category);
  const group = accessoryGroups.find((item) => item.id === params?.category);
  const [catalogBrand, setCatalogBrand] = useState<AccessoryCategoryBrowserBrand>("All brands");

  if (!detail || !group) return <NotFound />;

  const photo = categoryPhotos[accessoryGroups.findIndex((item) => item.id === group.id)] ?? realPhotos.accessories.restroom;
  const url = `${siteConfig.url}/commercial-restroom-accessories/${detail.id}`;
  const comparisonSections = getAccessoryComparisonSectionsForGroup(group.id);
  const comparisonGroups = comparisonSections.flatMap((section) => section.groups);
  const browserGroupId = group.id as AccessoryCategoryBrowserGroupId;
  const catalogCounts = getAccessoryCategoryBrowserCounts(browserGroupId);
  const featuredCatalogProducts = getAccessoryCategoryBrowserFeaturedProducts(browserGroupId, catalogBrand);
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: detail.headline,
      description: detail.overview,
      url,
      isPartOf: { "@id": `${siteConfig.url}/#website` },
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: group.items.length,
        itemListElement: group.items.map((item, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.name,
        })),
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: siteConfig.url },
        { "@type": "ListItem", position: 2, name: "Commercial Restroom Accessories", item: `${siteConfig.url}/commercial-restroom-accessories` },
        { "@type": "ListItem", position: 3, name: group.name, item: url },
      ],
    },
    ...(comparisonGroups.length > 0
      ? [{
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: `${group.name} Bobrick, ASI, and Bradley model comparisons`,
          numberOfItems: comparisonGroups.length,
          itemListElement: comparisonGroups.map((comparison, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: comparison.title,
            url: `${url}#compare-${comparison.id}`,
            description: `Three visual commercial restroom accessory options: ${comparison.options.map((option) => `${option.brand} ${option.model}`).join("; ")}. ${comparison.comparisonNote} Confirm project requirements before selecting a final specification.`,
            image: comparison.options.map((option) => `${siteConfig.url}${getAccessoryComparisonProductImage(comparison.rank, option.brand, "").src}`),
          })),
        }]
      : []),
  ];

  const related = accessoryCategoryDetails.filter((item) => item.id !== detail.id).slice(0, 3);

  return (
    <>
      <StructuredData data={schema} />
      <header className="simple-page-hero section-line">
        <div className="simple-page-hero__copy">
          <p className="breadcrumb"><Link href="/">Home</Link><span>/</span><Link href="/commercial-restroom-accessories">Accessories</Link><span>/</span>{group.name}</p>
          <span className="kicker">{detail.index} / {detail.eyebrow}</span>
          <h1>{detail.headline}</h1>
          <p>{detail.overview}</p>
          <div className="button-row">
            <Link href="/request-a-quote" className="button button--primary">Quote These Accessories <ArrowUpRight aria-hidden="true" /></Link>
            <Link href="/commercial-restroom-accessories" className="button button--outline">View Full Catalog</Link>
          </div>
        </div>
        <figure>
          <img
            src={photo.large}
            srcSet={`${photo.small} 720w, ${photo.large} 1280w`}
            sizes="(max-width: 820px) 100vw, 50vw"
            width="1280"
            height="858"
            alt={`${group.name} in a completed commercial restroom`}
          />
          <figcaption>{group.name} products for coordinated commercial restroom packages.</figcaption>
        </figure>
      </header>

      <section className="category-proof section-line" aria-label={`${group.name} buyer groups`}>
        <div><Users aria-hidden="true" /><span>Often specified by</span></div>
        {detail.buyerTypes.map((buyer, index) => <p key={buyer}><span>0{index + 1}</span>{buyer}</p>)}
      </section>

      <section id="category-price-references" className="accessory-category-browser section-line section-anchor" aria-labelledby="accessory-category-browser-title">
        <div className="accessory-category-browser__heading">
          <div>
            <span className="kicker">Browse by brand</span>
            <h2 id="accessory-category-browser-title">See owner-supplied model references before you compare.</h2>
          </div>
          <p>Use the brand selector to view a compact set of owner-supplied category models and their reference prices. The same product card size is used for every brand. Reference prices are not a checkout total; exact configuration, finish, freight, tax, availability, and final project price are confirmed in writing.</p>
        </div>

        <div className="accessory-category-browser__filters" role="tablist" aria-label={`${group.name} catalog brands`}>
          {accessoryCategoryBrowserBrands.map((brand) => {
            const selected = catalogBrand === brand;
            const count = brand === "All brands" ? catalogCounts.all : catalogCounts[brand];
            const visual = brand === "All brands" ? null : accessoryComparisonBrandVisuals[brand];

            return (
              <button
                type="button"
                key={brand}
                className={selected ? "is-selected" : undefined}
                role="tab"
                aria-selected={selected}
                aria-controls="category-price-panel"
                onClick={() => setCatalogBrand(brand)}
              >
                {visual ? <img src={visual.logo} width={visual.logoWidth} height={visual.logoHeight} alt="" /> : <strong>All brands</strong>}
                <span>{count ? `${count} priced model${count === 1 ? "" : "s"}` : "Quote-led"}</span>
              </button>
            );
          })}
        </div>

        {featuredCatalogProducts.length ? (
          <div id="category-price-panel" className="accessory-category-browser__products" role="tabpanel" aria-label={`${catalogBrand} ${group.name} price references`}>
            {featuredCatalogProducts.map((product) => (
              <article className="accessory-category-browser__product" key={product.id}>
                <div className="accessory-category-browser__brand">
                  <img
                    src={accessoryComparisonBrandVisuals[product.brand].logo}
                    width={accessoryComparisonBrandVisuals[product.brand].logoWidth}
                    height={accessoryComparisonBrandVisuals[product.brand].logoHeight}
                    alt={accessoryComparisonBrandVisuals[product.brand].logoAlt}
                    loading="lazy"
                    decoding="async"
                  />
                  <span>{product.category}</span>
                </div>
                <figure>
                  <img src={product.image} width="720" height="720" alt={`${product.brand} ${product.model} — ${product.imageLabel.toLowerCase()}`} loading="lazy" decoding="async" />
                  <figcaption>{product.imageLabel}</figcaption>
                </figure>
                <div className="accessory-category-browser__product-copy">
                  <span>Model reference</span>
                  <h3>{product.model}</h3>
                  <p>{product.description}</p>
                  <div>
                    <span>Reference price</span>
                    <strong>{formatAccessoryCurrency(product.price)}</strong>
                  </div>
                  <Link href="/accessory-quote-cart" className="text-link">Open saved Quote List <ArrowRight aria-hidden="true" /></Link>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div id="category-price-panel" className="accessory-category-browser__empty" role="tabpanel">
            <p><strong>These specialty products are quote-led.</strong> A verified owner-supplied price line is not currently published for this category. Share the function, model if known, wall condition, quantity, and project location so the correct product can be reviewed.</p>
            <Link href="/request-a-quote" className="button button--primary">Request specialty accessory help <ArrowUpRight aria-hidden="true" /></Link>
          </div>
        )}

        <p className="accessory-category-browser__note"><strong>Important:</strong> {catalogBrand === "All brands" ? "The All Brands view shows one documented starting model per brand in this category." : `The ${catalogBrand} view shows three listed models from the owner-supplied reference-price catalog.`} Products are not automatically interchangeable; compare the documented model details below and confirm project conditions before selection.</p>
      </section>

      <section className="content-section section-line" aria-labelledby="category-products-title">
        <div className="catalog-heading">
          <div><span className="kicker">Product coverage</span><h2 id="category-products-title">What belongs in this category.</h2></div>
          <p>Use the product descriptions to organize the scope. Exact series, sizes, finishes, capacities, and mounting conditions are confirmed for the project rather than assumed from a generic list.</p>
        </div>
        <div className="expanded-product-grid">
          {group.items.map((item, index) => (
            <article key={item.name}>
              <div><span>{String(index + 1).padStart(2, "0")}</span><PackageSearch aria-hidden="true" /></div>
              <h3>{item.name}</h3>
              <p>{item.description}</p>
              <dl><dt>Common configurations</dt><dd>{item.configurations}</dd><dt>Confirm before pricing</dt><dd>{item.specify}</dd></dl>
            </article>
          ))}
        </div>
      </section>

      {comparisonSections.map((section) => (
        <section id={section.id} className="accessory-model-comparison section-line section-anchor" aria-labelledby={`${section.id}-title`} key={section.id}>
          <div className="accessory-model-comparison__intro">
            <div><span className="kicker">{section.eyebrow}</span><h2 id={`${section.id}-title`}>{section.title}</h2></div>
            <p>{section.intro}</p>
          </div>

          <div className="accessory-model-comparison__selection-guide" aria-label="How to compare these accessory options">
            <div>
              <span className="kicker">{accessoryComparisonSelectionGuide.eyebrow}</span>
              <h3>{accessoryComparisonSelectionGuide.title}</h3>
            </div>
            <p>{accessoryComparisonSelectionGuide.copy}</p>
            <ol>
              <li><span>01</span><strong>Compare the model family</strong></li>
              <li><span>02</span><strong>Confirm the project conditions</strong></li>
              <li><span>03</span><strong>Request a focused quote</strong></li>
            </ol>
          </div>

          <div className="accessory-model-comparison__groups">
            {section.groups.map((comparison) => (
              <article className="accessory-model-comparison__group" id={`compare-${comparison.id}`} key={comparison.id}>
                <header>
                  <span>{String(comparison.rank).padStart(2, "0")}</span>
                  <div><h3>{comparison.title}</h3><p>{comparison.comparisonNote}</p></div>
                </header>
                <div className="accessory-model-comparison__options" role="list" aria-label={`${comparison.title} comparable models`}>
                  {comparison.options.map((option, optionIndex) => {
                    const brandVisual = accessoryComparisonBrandVisuals[option.brand];
                    const productImage = getAccessoryComparisonProductImage(
                      comparison.rank,
                      option.brand,
                      `${option.brand} ${option.model} ${option.product}`,
                    );

                    return (
                      <article className="accessory-model-card" role="listitem" key={`${comparison.id}-${option.brand}`}>
                        <div className="accessory-model-card__brand">
                          <span className="accessory-model-card__option-number">Option {String(optionIndex + 1).padStart(2, "0")}</span>
                          <img
                            src={brandVisual.logo}
                            width={brandVisual.logoWidth}
                            height={brandVisual.logoHeight}
                            alt={brandVisual.logoAlt}
                            loading="lazy"
                            decoding="async"
                          />
                          <span className="accessory-model-card__brand-name">{option.brand}</span>
                        </div>
                        <figure className="accessory-model-card__product">
                          <img
                            src={productImage.src}
                            width={productImage.width}
                            height={productImage.height}
                            alt={productImage.alt}
                            loading="lazy"
                            decoding="async"
                          />
                          {productImage.representative ? <figcaption>Representative family image</figcaption> : null}
                        </figure>
                        <div className="accessory-model-card__body">
                          <span className="accessory-model-card__eyebrow">Model reference</span>
                          <h4>{option.model}</h4>
                          <dl className="accessory-model-card__specification">
                            <div><dt>Product type</dt><dd>{option.product}</dd></div>
                          </dl>
                          <p className="accessory-model-card__details">{option.details}</p>
                          {productImage.note ? <p className="accessory-model-card__reference-note">{productImage.note}</p> : null}
                          {option.qualification ? <p className="accessory-model-card__qualification">{option.qualification}</p> : null}
                          <Link href="/request-a-quote" className="accessory-model-card__quote" aria-label={`Request a quote for ${option.brand} ${option.model}`}>
                            Request a quote <ArrowUpRight aria-hidden="true" />
                          </Link>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </article>
            ))}
          </div>

          <div className="accessory-model-comparison__footer">
            <p><strong>Comparable does not mean interchangeable.</strong> Confirm dimensions, mounting, capacity, finish, utilities, accessibility documentation, and the project specification before ordering.</p>
            <Link href="/request-a-quote" className="button button--primary">Quote These Models <ArrowUpRight aria-hidden="true" /></Link>
          </div>
        </section>
      ))}

      <section className="planning-panel section-line" aria-labelledby="planning-checks-title">
        <div className="planning-panel__heading"><ClipboardCheck aria-hidden="true" /><span className="kicker">Specification checkpoints</span><h2 id="planning-checks-title">Resolve the conditions that shape the selection.</h2></div>
        <ol>{detail.planningChecks.map((item, index) => <li key={item}><span>0{index + 1}</span><Check aria-hidden="true" /><p>{item}</p></li>)}</ol>
      </section>

      <section className="coordination-note section-line">
        <div><span className="red-square red-square--large" aria-hidden="true" /><span className="kicker">For a useful quote</span></div>
        <div><h2>Send the room—not only a product name.</h2><p>{detail.coordination}</p><Link href="/resources/how-to-measure" className="text-link">See the project input guide <ArrowRight aria-hidden="true" /></Link></div>
      </section>

      <section className="related-links section-line" aria-labelledby="related-accessories-title">
        <div><span className="kicker">Continue the package</span><h2 id="related-accessories-title">Related accessory groups.</h2></div>
        <div>{related.map((item) => <Link key={item.id} href={`/commercial-restroom-accessories/${item.id}`}><span>{item.index}</span><strong>{accessoryGroups.find((groupItem) => groupItem.id === item.id)?.name}</strong><ArrowRight aria-hidden="true" /></Link>)}</div>
      </section>

      <QuoteBand title={`Request a ${group.name.toLowerCase()} quote.`} copy="Share the accessory schedule, plans, quantities, finishes, mounting conditions, and delivery location. If the scope is still developing, send what is known and we will identify the next information needed." />
    </>
  );
}
